const FOOTER_GAP = 12;
const HEADING_SELECTOR = 'h2, h3, .assignment-section-kicker';
// Parts that move to the next page whole. [data-keep-together] groups rows that must stay together.
const ATOMIC_SELECTOR = [
	'header', 'p', 'li', '.quotation-scope-line',
	'.info-row', '.schedule-row', '.pricing-row',
	'.signatures', '.assignment-signatures',
	'[data-keep-together]',
].join(', ');

const collectParts = (block) => {
	const parts = [];
	const visit = (node) => {
		if (node.matches('thead, colgroup')) return;
		if (node.matches('.quotation-scope-row')) {
			node.querySelectorAll('.quotation-scope-line').forEach(visit);
			return;
		}
		if (node.matches(HEADING_SELECTOR) || node.matches(ATOMIC_SELECTOR) || textTarget(node) ||
			node.tagName === 'TR' || !node.children.length) {
			parts.push(node);
			return;
		}
		Array.from(node.children).forEach(visit);
	};
	Array.from(block.children).forEach(visit);
	return parts;
};

const textTarget = (node) => {
	// Rows marked to keep together move to the next page whole instead of splitting mid-text.
	if (node.closest('[data-keep-together]')) return null;
	if (node.matches('p, li, .quotation-scope-line')) return node;
	if (node.matches('tr, .info-row, .schedule-row, .pricing-row')) {
		return Array.from(node.children)
			.filter((child) => !child.querySelector('table, ul, ol, img'))
			.sort((a, b) => b.textContent.length - a.textContent.length)[0] || null;
	}
	if (node.textContent.trim() && Array.from(node.children).every((child) =>
		child.matches('span, strong, em, b, i, br, a, u, small, sub, sup, code'))) return node;
	return null;
};

// DOM ranges preserve inline formatting when a paragraph crosses a page boundary.
const sliceText = (node, from, to) => {
	const document = node.ownerDocument;
	const walker = document.createTreeWalker(node, 4 /* SHOW_TEXT */);
	const range = document.createRange();
	let position = 0;
	let started = false;
	let text;
	while ((text = walker.nextNode())) {
		const end = position + text.textContent.length;
		if (!started && from <= end) {
			range.setStart(text, Math.max(0, from - position));
			started = true;
		}
		if (started && to <= end) {
			range.setEnd(text, Math.max(0, to - position));
			return range.cloneContents();
		}
		position = end;
	}
	return document.createDocumentFragment();
};

const clonePart = ({ node, from = 0, to = null }) => {
	const clone = node.cloneNode(true);
	if (to !== null) {
		const target = textTarget(node);
		const cloneTarget = target === node
			? clone
			: clone.children[Array.from(node.children).indexOf(target)];
		cloneTarget.replaceChildren(sliceText(target, from, to));
		cloneTarget.style.overflowWrap = 'anywhere';
		if (from > 0 && target !== node) {
			Array.from(clone.children).forEach((cell) => {
				if (cell !== cloneTarget) cell.replaceChildren();
			});
		}
	}
	if (node.tagName === 'LI') {
		if (node.parentElement.tagName === 'OL') {
			clone.value = node.hasAttribute('value') ? Number(node.getAttribute('value')) :
				(node.parentElement.hasAttribute('start') ? Number(node.parentElement.getAttribute('start')) : 1) +
				Array.from(node.parentElement.children).indexOf(node);
		}
		if (from > 0) clone.style.listStyleType = 'none';
	}
	if (from > 0 && clone.classList.contains('quotation-scope-line--bullet')) {
		clone.classList.replace('quotation-scope-line--bullet', 'quotation-scope-line--bullet-continuation');
	}
	return clone;
};

const renderChunk = (block, entries) => {
	const chunk = block.cloneNode(false);
	const ancestors = new Map([[block, chunk]]);
	const ensureParent = (source, entry) => {
		if (ancestors.has(source)) return ancestors.get(source);
		const parent = ensureParent(source.parentElement, entry);
		const clone = source.cloneNode(false);
		// Repeated column labels preserve the table layout; section headings are
		// ordinary entries and are emitted only once.
		if (source.tagName === 'TABLE') {
			source.querySelectorAll(':scope > colgroup, :scope > thead')
				.forEach((child) => clone.appendChild(child.cloneNode(true)));
		}
		if (source.matches('.quotation-scope-row')) {
			const label = source.querySelector('th')?.cloneNode(true);
			if (label) {
				if (entry.node !== source.querySelector('.quotation-scope-line') || entry.from > 0) {
					label.replaceChildren();
				}
				clone.appendChild(label);
			}
		}
		parent.appendChild(clone);
		ancestors.set(source, clone);
		return clone;
	};
	entries.forEach((entry) => {
		ensureParent(entry.node.parentElement, entry).appendChild(clonePart(entry));
	});
	return chunk;
};

const pageFits = (page, footerSelector) => {
	const footer = page.querySelector(footerSelector);
	const bottom = Array.from(page.children).reduce((value, child) =>
		child === footer ? value : Math.max(value, child.offsetTop + child.offsetHeight), 0);
	return bottom <= (footer?.offsetTop || page.clientHeight) - FOOTER_GAP;
};

/**
 * Fill measured pages, splitting text and lists without repeating section titles.
 * A page may start with a running header (headerSelector); it is not content of the document.
 */
export const paginateDocumentFlow = ({ root, blocks, createPage, footerSelector, headerSelector = null }) => {
	const pages = [];
	let page;
	const nextPage = () => {
		page = createPage(pages.length + 1);
		root.appendChild(page);
		pages.push(page);
	};
	const append = (node) => page.insertBefore(node, page.querySelector(footerSelector));
	const hasContent = () => Array.from(page.children).some((child) =>
		!child.matches(footerSelector) && !(headerSelector && child.matches(headerSelector)));
	nextPage();

	for (const block of blocks) {
		const whole = block.cloneNode(true);
		append(whole);
		if (pageFits(page, footerSelector)) continue;
		whole.remove();

		let entries = [];
		let chunk = null;
		let headings = [];
		const show = (candidate) => {
			chunk?.remove();
			chunk = candidate.length ? renderChunk(block, candidate) : null;
			if (chunk) append(chunk);
			return pageFits(page, footerSelector);
		};
		const advance = () => {
			show(entries);
			nextPage();
			entries = [];
			chunk = null;
		};

		for (const node of collectParts(block)) {
			if (node.matches(HEADING_SELECTOR)) {
				headings.push({ node });
				continue;
			}
			const target = textTarget(node);
			const text = target?.textContent || '';
			let from = 0;
			let complete = false;
			while (!complete) {
				const entry = from ? { node, from, to: text.length } : { node };
				const candidate = [...entries, ...headings, entry];
				if (show(candidate)) {
					entries = candidate;
					headings = [];
					complete = true;
					continue;
				}

				const boundaries = target
					? Array.from(text.matchAll(/\s+/g), (match) => match.index + match[0].length)
						.filter((end) => end > from && end < text.length && text.slice(from, end).trim())
					: [];
				const findSplit = (positions) => {
					let low = 0;
					let high = positions.length - 1;
					let result = 0;
					while (low <= high) {
						const middle = Math.floor((low + high) / 2);
						if (show([...entries, ...headings, { node, from, to: positions[middle] }])) {
							result = positions[middle];
							low = middle + 1;
						} else {
							high = middle - 1;
						}
					}
					return result;
				};
				let splitAt = findSplit(boundaries);
				show(entries);
				if (!splitAt && target && !hasContent()) {
					let offset = from;
					const characters = Array.from(text.slice(from), (character) => (offset += character.length));
					splitAt = findSplit(characters.slice(0, -1));
				}
				if (splitAt) {
					entries = [...entries, ...headings, { node, from, to: splitAt }];
					headings = [];
					from = splitAt;
					advance();
					continue;
				}

				show(entries);
				if (hasContent()) {
					advance();
					continue;
				}
				// Do not silently clip an element that cannot fit even on an empty page.
				throw new Error('A document element is taller than the available page area.');
			}
		}
		if (headings.length) {
			if (!show([...entries, ...headings])) advance();
			entries = [...entries, ...headings];
		}
		show(entries);
	}
	return pages;
};
